import { cn } from "@gymflow/helpers";
import { VariantProps } from "class-variance-authority";
import omit from "lodash/omit";
import React, {
  forwardRef,
  MouseEvent,
  ReactNode,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";

import {
  buttonSpinnerVariants,
  buttonVariants,
  disabledButtonVariants,
} from "./buttonVariants";
import CustomTooltip from "./CustomTooltip";
import { Spinner } from "./Spinner";

interface ButtonProps
  extends Omit<React.HTMLProps<HTMLButtonElement>, "type" | "size" | "onClick">,
    VariantProps<typeof buttonVariants> {
  showSpinner?: boolean;
  badgeContent?: ReactNode;
  type?: "button" | "submit";
  onClick?: (
    e: MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => Promise<void> | void;
  href?: string;
  link?: string;
  tooltip?: string;
}

export const Button = forwardRef<React.ElementRef<"button">, ButtonProps>(
  function Button(
    {
      children,
      className,
      showSpinner,
      size,
      intent,
      type = "button",
      tooltip,
      badgeContent,
      ...props
    }: ButtonProps,
    ref,
  ) {
    const [buttonId] = useState(Math.random().toString(36).slice(2));
    const [isLoading, setIsLoading] = useState(false);
    const buttonProps = useMemo(
      () => omit(props, ["disabled", "onClick"]),
      [props],
    );
    const Component = props.href
      ? ("a" as React.ElementType)
      : props.link
        ? Link
        : ("button" as React.ElementType);
    const result = (
      <Component
        ref={ref}
        type={type}
        before={badgeContent}
        key={`button-${buttonId}`}
        {...(props.link ? { to: props.link } : {})}
        {...(props.href ? { href: props.href } : {})}
        className={cn(
          buttonVariants({ size, intent }),
          {
            [disabledButtonVariants({ intent })]: props.disabled || isLoading,
          },
          {
            "after:content-[attr(before)] after:font-medium after:text-secondary-700 after:bg-secondary-25 after:border-secondary-200 relative after:absolute after:right-0 after:top-0 after:flex after:aspect-square after:h-6 after:-translate-y-1/2 after:translate-x-1/2 after:items-center after:justify-center after:rounded-full after:border after:p-1 after:text-xs":
              !!badgeContent,
          },
          className,
        )}
        onClick={async (e: MouseEvent<HTMLButtonElement, MouseEvent>) => {
          const result = props.onClick?.(e);
          if (result instanceof Promise) {
            setIsLoading(true);
            try {
              await result;
            } catch {
              setIsLoading(false);
            }
            setIsLoading(false);
          }
        }}
        disabled={props.disabled || isLoading}
        {...buttonProps}
      >
        <Spinner
          className={cn({
            hidden: !(isLoading || showSpinner),
          })}
          pathClassName={buttonSpinnerVariants({ intent })}
        />
        {!(isLoading || showSpinner) && children}
      </Component>
    );

    if (tooltip)
      return (
        <CustomTooltip asChild message={tooltip}>
          {result}
        </CustomTooltip>
      );
    return result;
  },
);
